import produce from 'immer'

const prefix = 'QUEUE/'

const generateAction = ({
    baseActionCreatorName,
    baseActionName,
    prefix: actionTypePrefix = '',
    handleAction,
}) => {
    const actionTypesMap = {
        base: `${actionTypePrefix}${baseActionName}`,
        success: `${actionTypePrefix}${baseActionName}_SUCCESS`,
        error: `${actionTypePrefix}${baseActionName}_ERROR`,
    }

    const actionCreatorsMap = {
        base: baseActionCreatorName,
        success: `${baseActionCreatorName}Success`,
        error: `${baseActionCreatorName}Error`,
    }

    const actions = {
        baseAction: {
            type: actionTypesMap.base,
            actionCreatorName: actionCreatorsMap.base,
            creator: () => ({ payload } = {}) => ({
                type: actionTypesMap.base,
                ...payload,
            }),
        },
        successAction: {
            type: actionTypesMap.success,
            actionCreatorName: actionCreatorsMap.success,
            creator: () => ({ payload } = {}) => ({
                type: actionTypesMap.success,
                payload,
            }),
        },
        errorAction: {
            type: actionTypesMap.error,
            actionCreatorName: actionCreatorsMap.error,
            creator: () => ({ payload, error } = {}) => ({
                type: actionTypesMap.error,
                payload,
                error,
            }),
        },
    }

    const actionsArray = Object.values(actions)

    const types = actionsArray.map(({ type }) => type)

    const creators = () => actionsArray.reduce((acc, { creator, actionCreatorName }) => {
        /* eslint-disable-next-line no-param-reassign */
        acc[actionCreatorName] = creator()
        return acc
    }, {})

    return {
        types,
        creators,
        handleAction,
        ...actionTypesMap,
    }
}

const create = generateAction({
    prefix,
    baseActionCreatorName: 'create',
    baseActionName: 'CREATE',
    handleAction: (
        { items } = {},
        { dispatch } = {},
    ) => {
        try {
            if (!(items?.length >= 0)) {
                throw new Error('cannot create queue')
            }

            dispatch({
                type: create.success,
                payload: {
                    items,
                },
            })
        } catch (error) {
            dispatch({
                type: create.error,
                error: error.message,
            })
        }
    },
})

const insert = generateAction({
    prefix,
    baseActionCreatorName: 'insert',
    baseActionName: 'INSERT',
    handleAction: (
        { items } = {},
        { dispatch } = {},
    ) => {
        try {
            if (!(items?.length >= 0)) {
                throw new Error('cannot insert items')
            }

            dispatch({
                type: insert.success,
                payload: {
                    items,
                },
            })
        } catch (error) {
            dispatch({
                type: insert.error,
                error: error.message,
            })
        }
    },
})

const remove = generateAction({
    prefix,
    baseActionCreatorName: 'remove',
    baseActionName: 'REMOVE',
    handleAction: (
        action,
        { dispatch } = {},
    ) => {
        try {
            dispatch({
                type: remove.success,
            })
        } catch (error) {
            dispatch({
                type: remove.error,
                error: error.message,
            })
        }
    },
})

const QueueFactory = ({
    reducerName = `queue_middleware_${Date.now()}`,
} = {}) => {
    const Queue = {
        reducerName,
        types: {
            ...create.types,
            ...insert.types,
            ...remove.types,
        },
        creators: {
            ...create.creators(),
            ...insert.creators(),
            ...remove.creators(),
        },
        initialState: {
            isCreating: null,
            didCreate: null,
            items: null,
            error: null,

            didInsert: null,
            isInserting: null,

            didRemove: null,
            isRemoving: null,
        },
        reducer: (
            state = Queue.initialState,
            { type, payload, error } = {},
        ) => produce(state, (draftState) => {
            switch (type) {
                case create.base:
                    draftState.isCreating = true
                    draftState.didCreate = Queue.initialState.didCreate
                    draftState.items = Queue.initialState.items
                    draftState.error = Queue.initialState.error

                    draftState.didInsert = Queue.initialState.didInsert
                    draftState.isInserting = Queue.initialState.isInserting

                    draftState.didRemove = Queue.initialState.didRemove
                    draftState.isRemoving = Queue.initialState.isRemoving
                    break

                case create.success:
                    draftState.isCreating = false
                    draftState.didCreate = true
                    draftState.items = payload?.items
                    break

                case create.error:
                    draftState.isCreating = false
                    draftState.didCreate = false
                    draftState.error = error || 'create queue error occured'
                    break

                case insert.base:
                    draftState.isInserting = true
                    draftState.didInsert = Queue.initialState.didInsert
                    draftState.error = Queue.initialState.error
                    break

                case insert.success:
                    draftState.isInserting = false
                    draftState.didInsert = true
                    payload?.items.forEach((item) => draftState.items.push(item))
                    break

                case insert.error:
                    draftState.isInserting = false
                    draftState.didInsert = false
                    draftState.error = error || 'insert item error occured'
                    break

                case remove.base:
                    draftState.isRemoving = true
                    draftState.didRemove = Queue.initialState.didRemove
                    draftState.error = Queue.initialState.error
                    break

                case remove.success:
                    draftState.isRemoving = false
                    draftState.didRemove = true
                    draftState.items.shift()
                    break

                case remove.error:
                    draftState.isRemoving = false
                    draftState.didRemove = false
                    draftState.error = error || 'remove item error occured'
                    break

                default:
                    // do nothing
            }
        }),
        selectors: {
            queue: (state) => state?.[Queue.reducerName],
            items: (state) => Queue.selectors.queue(state)?.items,
            length: (state) => Queue.selectors.items(state)?.length,
            getFirst: (state) => Queue.selectors.items(state)?.[0],
        },
        handleAction: (action, storeAPI) => {
            switch (action?.type) {
                case create.base:
                    create.handleAction(action, storeAPI)
                    break

                case insert.base:
                    insert.handleAction(action, storeAPI)
                    break

                case remove.base:
                    remove.handleAction(action, storeAPI)
                    break

                default:
                    // do nothing
            }
        },
    }

    return Queue
}

export default QueueFactory
